import * as React from 'react';
import { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Column, TableInstance, useFilters, usePagination, useSortBy, useTable } from 'react-table';
import { useMemo } from 'react';
import { useDefaultColumn } from '../../hooks/ReactTableHooks';
import { Button, Navbar, NavbarBrand } from 'reactstrap';
import { FaChevronDown, FaChevronUp, FaPlus } from 'react-icons/fa';
import { TablePagination } from '../TablePagination/TablePagination';
import { IContactDetails } from '../Clients/ClientContext';
import { authAxios } from '../../services/AxiosService';

interface IProps {
  selectRow?: (userId: string) => void;
}

export interface IGladueMember {
  gladueMemberId?: string;
  firstName: string;
  lastName: string;
  contactDetails: IContactDetails[];
  firstNation: string;
  location: string;
  isActive: boolean;
  roles: string[];
}

export const GladueMemberTable: React.FC<IProps> = (props) => {
  const [gladueMembers, setGladueMembers] = useState<IGladueMember[]>([]);
  const history = useHistory();
  useEffect(() => {
    authAxios.get('/api/gladue-members').then((response) => {
      setGladueMembers(response.data);
    });
  }, []);

  const columns: Column<IGladueMember>[] = useMemo(
    () => [
      {
        Header: 'Last Name',
        accessor: 'lastName',
      },
      {
        Header: 'First Name',
        accessor: 'firstName',
      },
      {
        Header: 'Role',
        id: 'roles',
        accessor: (member: IGladueMember) => {
          return member.roles.join(', ');
        },
      },
    ],
    []
  );
  const data: IGladueMember[] = useMemo(() => gladueMembers, [gladueMembers]);

  const defaultColumn = useDefaultColumn();

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,

    prepareRow,

    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  }: TableInstance<IGladueMember> = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: { pageSize: 20 },
    },
    useFilters,
    useSortBy,
    usePagination
  );

  const selectRow = (gladueMemberId: string) => {
    if (props.selectRow) {
      props.selectRow(gladueMemberId);
    } else {
      history.push('/gladue-members/' + gladueMemberId);
    }
  };

  return (
    <div>
      <Navbar color={'light'} light={true} expand={'xs'}>
        <NavbarBrand className='mr-auto'>Gladue Members</NavbarBrand>
        <Button color={'primary'} tag={Link} to={{ pathname: '/gladue-members/add' }}>
          <FaPlus className='mr-2' />
          Add Gladue Member
        </Button>
      </Navbar>
      <div className={'table-responsive'}>
        <table className={'table table-bordered table-hover'} {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup: any, index: number) => (
              <tr key={`gladue-members-table-thead-tr-${index}`} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: any) => (
                  <th
                    key={`gladue-members-table-thead-tr-${index}-${column.id}`}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    {column.render('Header')}
                    <span>{column.isSorted && (column.isSortedDesc ? <FaChevronDown /> : <FaChevronUp />)}</span>
                    <div onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
                      {column.canFilter ? column.render('Filter') : null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()} style={{ cursor: 'pointer' }}>
            {page.map((row: any) => {
              prepareRow(row);

              return (
                <tr key={`gladue-members-table-tr-${row.id}`} {...row.getRowProps()}>
                  {row.cells.map((cell: any) => {
                    return (
                      <td
                        key={`glaude-members-table-td-${cell.row.id}-${cell.column.id}`}
                        {...cell.getCellProps()}
                        onClick={() => selectRow(row.original.gladueMemberId)}
                      >
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <TablePagination
        pageCount={pageCount}
        pageOptions={pageOptions}
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        gotoPage={gotoPage}
        previousPage={previousPage}
        nextPage={nextPage}
        setPageSize={setPageSize}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageSizes={[20, 50, 100, 500]}
      />
    </div>
  );
};
